import { Chip, Tooltip } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { type RowTransferState } from "@/features/salesforceIntegration/utils/resolveRowTransferState";

interface Props {
  state: RowTransferState;
}

/**
 * 同期アイコンを回し続け、処理が進んでいることを伝える。
 * 動きを減らす設定の利用者には回さない。
 */
const SPINNING_ICON_SX = {
  animation: "salesforce-transfer-spin 1.4s linear infinite",
  "@keyframes salesforce-transfer-spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
} as const;

/**
 * FeedGridのSalesforce列に出す、行単位の連携状態。
 *
 * 連携できない理由と一部連携済みの内訳はツールチップに逃がし、
 * 列の幅を圧迫せずに一覧性を保つ。
 */
export const SalesforceTransferStateCell: React.FC<Props> = ({ state }) => {
  switch (state.kind) {
    case "UNKNOWN":
      return null;

    case "TRANSFERABLE":
      return (
        <Chip label="連携可" size="small" variant="outlined" color="primary" />
      );

    case "PARTIALLY_TRANSFERRED":
      return (
        <Tooltip
          title={`${formatNumberWithComma(
            state.totalCount
          )}件中${formatNumberWithComma(
            state.transferredCount
          )}件を連携済みです。`}
          placement="top"
          arrow
        >
          <span style={{ display: "inline-flex" }}>
            {/* 「連携可」と一目で見分けられるよう、青系ではなく警告色を使う */}
            <Chip
              label="一部連携済み"
              size="small"
              variant="outlined"
              color="warning"
            />
          </span>
        </Tooltip>
      );

    case "TRANSFERRED":
      return <Chip label="連携済み" size="small" />;

    case "RUNNING":
      return (
        <Chip
          label="連携中"
          size="small"
          color="info"
          icon={<SyncIcon fontSize="small" sx={SPINNING_ICON_SX} />}
        />
      );

    case "BLOCKED":
      return (
        <Tooltip
          title={state.reasonMessage}
          placement="top"
          arrow
          disableHoverListener={state.reasonMessage === ""}
        >
          <span style={{ display: "inline-flex" }}>
            <Chip
              label="連携不可"
              size="small"
              variant="outlined"
              icon={<InfoOutlinedIcon fontSize="small" />}
            />
          </span>
        </Tooltip>
      );

    default:
      return null;
  }
};
